// One shared vocabulary for displaying amenity / feature keys.
//
// Listings store snake_case keys ("bike_storage", "in_unit_laundry") plus
// occasional free text from older records. Raw keys must NEVER reach the UI:
// labelFor() prettifies anything, iconFor() picks a matching lucide icon, and
// groupAmenities() buckets them Airbnb-style for the detail page.
import {
  AirVent,
  ArrowUpDown,
  Accessibility,
  Bath,
  BedDouble,
  Bike,
  Briefcase,
  Building2,
  Bus,
  Car,
  ConciergeBell,
  Dumbbell,
  Flame,
  Footprints,
  GraduationCap,
  Grid2x2,
  Droplets,
  Hospital,
  KeyRound,
  Landmark,
  Layers,
  LockKeyhole,
  MapPin,
  Martini,
  MonitorPlay,
  Package,
  PawPrint,
  Plug,
  Refrigerator,
  Ruler,
  School,
  ShieldCheck,
  ShoppingCart,
  Shirt,
  Sofa,
  Sparkles,
  Store,
  Sun,
  Trees,
  TrainFront,
  Tv,
  UtensilsCrossed,
  Utensils,
  WashingMachine,
  Waves,
  Wifi,
  Wind,
  Warehouse,
  DoorOpen,
  CigaretteOff,
  Zap,
  Home,
} from "lucide-react";

// key -> { label, icon }. Covers every canonical wizard key plus common
// legacy phrasing. Anything unknown falls back to prettified text + a dot.
const META = {
  // Utilities
  heating: { label: "Heating included", icon: Flame },
  hydro: { label: "Hydro included", icon: Zap },
  water: { label: "Water included", icon: Droplets },
  internet: { label: "Internet included", icon: Wifi },
  cable: { label: "Cable TV", icon: Tv },
  satellite_tv: { label: "Satellite TV", icon: Tv },
  not_included: { label: "Utilities not included", icon: Plug },

  // Building
  gym: { label: "Gym", icon: Dumbbell },
  pool: { label: "Pool", icon: Waves },
  sauna: { label: "Sauna", icon: Sun },
  hot_tub: { label: "Hot tub", icon: Bath },
  rooftop_terrace: { label: "Rooftop terrace", icon: Building2 },
  party_room: { label: "Party room", icon: Martini },
  guest_suite: { label: "Guest suite", icon: BedDouble },
  bike_storage: { label: "Bike storage", icon: Bike },
  storage_locker: { label: "Storage locker", icon: Package },
  laundry_room: { label: "Laundry room", icon: WashingMachine },
  ev_charging: { label: "EV charging", icon: Plug },
  car_wash: { label: "Car wash", icon: Car },
  pet_wash: { label: "Pet wash", icon: PawPrint },
  media_room: { label: "Media room", icon: MonitorPlay },
  business_centre: { label: "Business centre", icon: Briefcase },
  elevator: { label: "Elevator", icon: ArrowUpDown },
  wheelchair_access: { label: "Wheelchair access", icon: Accessibility },
  concierge: { label: "Concierge", icon: ConciergeBell },
  security_guard: { label: "Security guard", icon: ShieldCheck },
  on_site_management: { label: "On site management", icon: KeyRound },
  controlled_access: { label: "Controlled access", icon: LockKeyhole },
  intercom: { label: "Intercom", icon: ConciergeBell },
  package_lockers: { label: "Package lockers", icon: Package },
  smoke_free_building: { label: "Smoke free building", icon: CigaretteOff },

  // Unit
  in_unit_laundry: { label: "In unit laundry", icon: WashingMachine },
  dishwasher: { label: "Dishwasher", icon: UtensilsCrossed },
  air_conditioning: { label: "Air conditioning", icon: AirVent },
  balcony: { label: "Balcony", icon: Sun },
  patio: { label: "Patio", icon: Trees },
  walk_in_closet: { label: "Walk in closet", icon: Shirt },
  hardwood_floors: { label: "Hardwood floors", icon: Layers },
  stainless_appliances: { label: "Stainless appliances", icon: Refrigerator },
  fireplace: { label: "Fireplace", icon: Flame },
  ensuite_bathroom: { label: "Ensuite bathroom", icon: Bath },
  walkout: { label: "Walkout", icon: DoorOpen },
  high_ceilings: { label: "High ceilings", icon: Ruler },
  quartz_counters: { label: "Quartz counters", icon: Sparkles },
  large_windows: { label: "Large windows", icon: Grid2x2 },
  storage: { label: "Storage", icon: Warehouse },
  furnished: { label: "Furnished", icon: Sofa },

  // Nearby
  public_transit: { label: "Public transit", icon: Bus },
  subway: { label: "Subway", icon: TrainFront },
  bus_stop: { label: "Bus stop", icon: Bus },
  grocery: { label: "Grocery nearby", icon: ShoppingCart },
  schools: { label: "Schools", icon: School },
  parks: { label: "Parks", icon: Trees },
  shopping: { label: "Shopping", icon: Store },
  restaurants: { label: "Restaurants", icon: Utensils },
  hospital: { label: "Hospital", icon: Hospital },
  highway: { label: "Highway access", icon: MapPin },
  waterfront: { label: "Waterfront", icon: Waves },
  downtown: { label: "Downtown", icon: Landmark },
  university: { label: "University", icon: GraduationCap },
  daycare: { label: "Daycare", icon: Home },
  gym_nearby: { label: "Gym nearby", icon: Dumbbell },

  // Parking
  garage: { label: "Garage parking", icon: Car },
  underground: { label: "Underground parking", icon: Car },
  driveway: { label: "Driveway parking", icon: Car },
  street: { label: "Street parking", icon: Car },
  outdoor: { label: "Outdoor parking", icon: Car },
  indoor: { label: "Indoor parking", icon: Car },
  covered: { label: "Covered parking", icon: Car },
  electric: { label: "EV parking", icon: Plug },
  valet: { label: "Valet parking", icon: ConciergeBell },
  no_parking: { label: "No parking", icon: Footprints },
};

// Legacy free-text phrases (older listings) -> canonical key.
const ALIASES = [
  [/in.?(suite|unit).{0,3}laundry|washer/i, "in_unit_laundry"],
  [/air.?con|a\/c\b/i, "air_conditioning"],
  [/dish.?washer/i, "dishwasher"],
  [/park(ing)?|garage/i, "garage"],
  [/gym|fitness/i, "gym"],
  [/pool/i, "pool"],
  [/balcon/i, "balcony"],
  [/concierge/i, "concierge"],
  [/elevator/i, "elevator"],
  [/pet/i, "pet_wash"],
  [/transit|subway|skytrain|metro/i, "public_transit"],
  [/storage/i, "storage"],
  [/roof.?top/i, "rooftop_terrace"],
  [/hardwood/i, "hardwood_floors"],
  [/stainless/i, "stainless_appliances"],
  [/heat|hydro|utilit/i, "heating"],
  [/furnish/i, "furnished"],
];

function normalize(raw) {
  const key = String(raw || "").trim();
  const snake = key.toLowerCase().replace(/[\s-]+/g, "_");
  if (META[snake]) return snake;
  for (const [re, target] of ALIASES) {
    if (re.test(key)) return target;
  }
  return null;
}

export function labelFor(raw) {
  const key = normalize(raw);
  if (key) return META[key].label;
  // Unknown free text: prettify ("pet_friendly units" -> "Pet friendly units")
  const text = String(raw || "").replaceAll("_", " ").trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function iconFor(raw) {
  const key = normalize(raw);
  return key ? META[key].icon : Sparkles;
}

// Airbnb-style grouping for the listing detail page.
const GROUPS = [
  { title: "In the home", keys: ["in_unit_laundry", "dishwasher", "air_conditioning", "balcony", "patio", "walk_in_closet", "hardwood_floors", "stainless_appliances", "fireplace", "ensuite_bathroom", "walkout", "high_ceilings", "quartz_counters", "large_windows", "storage", "furnished"] },
  { title: "Building", keys: ["gym", "pool", "sauna", "hot_tub", "rooftop_terrace", "party_room", "guest_suite", "bike_storage", "storage_locker", "laundry_room", "ev_charging", "car_wash", "pet_wash", "media_room", "business_centre", "elevator", "wheelchair_access", "concierge", "security_guard", "on_site_management", "controlled_access", "intercom", "package_lockers", "smoke_free_building"] },
  { title: "Parking", keys: ["garage", "underground", "driveway", "street", "outdoor", "indoor", "covered", "electric", "valet", "no_parking"] },
  { title: "Utilities included", keys: ["heating", "hydro", "water", "internet", "cable", "satellite_tv", "not_included"] },
  { title: "Around the neighbourhood", keys: ["public_transit", "subway", "bus_stop", "grocery", "schools", "parks", "shopping", "restaurants", "hospital", "highway", "waterfront", "downtown", "university", "daycare", "gym_nearby"] },
];

// Collect every displayable amenity string a listing carries: the legacy
// flat `amenities` array plus the wizard's `features` buckets + utilities.
export function collectAmenities(listing) {
  const out = [];
  const push = (v) => {
    if (v && !out.includes(v)) out.push(v);
  };
  (listing?.amenities || []).forEach(push);
  const f = listing?.features || {};
  ["unit_features", "building_features", "commercial_building", "nearby"].forEach(
    (bucket) => (f[bucket] || []).forEach(push)
  );
  (listing?.utilities || []).forEach((u) => u !== "not_included" && push(u));
  (listing?.parking_type || []).forEach((p) => p !== "no_parking" && push(p));
  return out;
}

export function groupAmenities(listing) {
  const raws = collectAmenities(listing);
  const used = new Set();
  const groups = [];
  for (const g of GROUPS) {
    const items = [];
    for (const raw of raws) {
      const key = normalize(raw);
      if (key && g.keys.includes(key) && !used.has(raw)) {
        used.add(raw);
        items.push(raw);
      }
    }
    if (items.length) groups.push({ title: g.title, items });
  }
  const other = raws.filter((r) => !used.has(r));
  if (other.length) groups.push({ title: "More", items: other });
  return groups;
}

// Strip HTML for surfaces that need plain text (cards, popups): the wizard's
// rich text editor stores sanitized HTML in listing.description.
export function plainDescription(html) {
  if (!html) return "";
  if (!/[<>]/.test(html)) return html;
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || "").replace(/\s+/g, " ").trim();
}

// Whether a description contains (sanitized) markup that should render as HTML.
export function isHtmlDescription(text) {
  return /<\/?(p|br|ul|ol|li|b|i|strong|em)\b/i.test(text || "");
}
